// termsOfServiceData.js
// Digi-bakery.com — Terms of Service (Bakers)
// Effective date: 04 November 2025 — Version 1.0

export const termsOfServiceData = {
  en: {
    title: "Terms of Service — Bakers — Digi-bakery.com",
    effectiveDate: "Effective date: 04 November 2025",
    version: "Version: 1.0",
    acceptance:
      'Acceptance: Checking the "I accept the Terms of Service" box constitutes full and binding acceptance of these Terms of Service.',
    content: `
1. Introduction
Digi-bakery.com ("Platform", "we", "us") is operated as a sole proprietorship based in Ontario, Canada. The Platform provides a Software-as-a-Service (SaaS) solution enabling independent bakers ("Baker", "Client", "you") to manage orders, gift cards, and customer interactions.
By creating an account or using the Platform, you agree to be bound by these Terms of Service ("Terms").

2. Scope of Services
The Platform provides:
- order management tools,
- customer management features,
- gift card management,
- integrations with third-party service providers (including payment processing).
The Platform does not sell baked goods, collect payments, or act as a reseller or intermediary.

3. Eligibility
You must:
- be legally authorized to operate a baking business in your jurisdiction,
- provide accurate and up-to-date business information,
- comply with all applicable laws and regulations.

4. Account Responsibility
You are solely responsible for:
- maintaining the confidentiality of your account credentials,
- all activities conducted through your account,
- the accuracy of information provided to customers.

5. Payments – Stripe Connect (Direct Charges)
5.1 Payment Processing Model
The Platform uses Stripe Connect – Direct Charges with Express accounts.
Each Baker must create and maintain a Stripe Express account and enters into a separate and independent agreement directly with Stripe.

5.2 Merchant of Record
The Baker is the sole merchant of record for all transactions processed via the Platform.
The Platform:
- does not process payments,
- does not hold, route, or control funds,
- does not issue refunds,
- does not intervene in disputes or chargebacks.

5.3 Stripe Responsibility
All payment processing, settlement, payouts, refunds, chargebacks, fraud analysis, compliance reviews, and account decisions are handled exclusively by Stripe, under the Stripe Services Agreement.

6. Taxes & Legal Compliance
The Baker is solely responsible for:
- collecting and remitting applicable taxes,
- issuing invoices or receipts,
- complying with consumer protection, food safety, and payment regulations.
The Platform provides no tax, legal, or accounting advice.

7. Data Protection
To the extent the Platform processes personal data on behalf of the Baker, the Data Processing Agreement (DPA) available on the Platform forms an integral part of these Terms.

8. Limitation of Liability
To the maximum extent permitted by law:
The Platform shall not be liable for any loss, damage, or interruption resulting from:
- payment failures or declined transactions,
- delayed, suspended, or withheld payouts,
- Stripe account suspension or termination,
- chargebacks, fraud, or customer disputes,
- actions or decisions taken by Stripe.
Nothing in these Terms excludes liability for gross negligence or willful misconduct directly attributable to the Platform.

9. Indemnification
The Baker agrees to indemnify, defend, and hold harmless the Platform from any claim, loss, liability, damage, or expense (including legal fees) arising from:
- payment processing activities,
- refunds, disputes, or chargebacks,
- customer complaints related to transactions,
- failure to comply with tax, consumer, or regulatory obligations.

10. Service Availability
The Platform is provided "as is" and "as available." We do not guarantee uninterrupted or error-free service.

11. Suspension & Termination
We may suspend or terminate access if:
- these Terms are violated,
- required information is missing or inaccurate,
- legal or regulatory obligations require it.
The Baker may terminate their account at any time.

12. Modifications
We may update these Terms. Material changes will require re-acceptance before continued use.

13. Governing Law & Jurisdiction
These Terms are governed by the laws of the Province of Ontario and the federal laws of Canada applicable therein.
Any dispute shall be subject to the exclusive jurisdiction of the courts of Ontario, Canada, unless mandatory consumer protection laws provide otherwise.

14. Contact
Digi-bakery.com
Email: support@digi-bakery.com
    `.trim(),
  },

  fr: {
    title: "Conditions Générales d’Utilisation — Boulangers — Digi-bakery.com",
    effectiveDate: "Date d’entrée en vigueur : 04 novembre 2025",
    version: "Version : 1.0",
    acceptance:
      'Acceptation : la validation de la case « J’accepte les Conditions d’utilisation » vaut acceptation pleine et entière des présentes Conditions Générales d’Utilisation.',
    content: `
1. Présentation
Digi-bakery.com (« Plateforme », « nous ») est exploitée sous forme d’entreprise individuelle basée en Ontario, Canada. La Plateforme fournit un service SaaS permettant aux boulangers indépendants (« Boulanger », « Client », « vous ») de gérer commandes, cartes-cadeaux et clients.
L’utilisation de la Plateforme vaut acceptation des présentes Conditions (« Conditions »).

2. Nature du service
La Plateforme fournit uniquement des outils logiciels. Elle ne vend aucun produit, n’encaisse aucun paiement et n’agit pas comme intermédiaire commercial.

3. Éligibilité
Vous devez :
- être légalement autorisé à exercer votre activité dans votre juridiction,
- fournir des informations exactes et à jour,
- respecter toutes les lois et réglementations applicables.

4. Compte et sécurité
Vous êtes seul responsable :
- de la confidentialité de vos identifiants,
- de toute activité réalisée via votre compte,
- de l’exactitude des informations communiquées à vos clients.

5. Paiements – Stripe Connect (Débits directs)
5.1 Modèle de traitement
La Plateforme utilise Stripe Connect – Débits directs avec des comptes Express.
Chaque Boulanger doit créer et maintenir un compte Stripe Express et conclut un contrat distinct et indépendant directement avec Stripe.

5.2 Commerçant responsable (Merchant of Record)
Le Boulanger est le seul commerçant responsable de l’ensemble des transactions effectuées via la Plateforme.
La Plateforme :
- ne traite aucun paiement,
- ne détient, ne redirige ni ne contrôle les fonds,
- n’effectue aucun remboursement,
- n’intervient dans aucun litige ou rétrofacturation.

5.3 Responsabilité de Stripe
Le traitement des paiements, la compensation, les versements, les remboursements, les rétrofacturations, l’analyse de fraude, les contrôles de conformité et les décisions liées au compte sont assurés exclusivement par Stripe, dans le cadre du contrat Stripe.

6. Taxes & conformité légale
Le Boulanger est seul responsable :
- de la collecte et du versement des taxes applicables,
- de l’émission des factures ou reçus,
- du respect des lois (consommation, sécurité alimentaire, paiement).
La Plateforme ne fournit aucun conseil fiscal, juridique ou comptable.

7. Données personnelles
Dans la mesure où la Plateforme traite des données personnelles pour le compte du Boulanger, l’Accord de Sous-traitance des Données (DPA) disponible sur la Plateforme fait partie intégrante des présentes Conditions.

8. Limitation de responsabilité
Dans la limite permise par la loi :
La Plateforme ne saurait être tenue responsable de toute perte, dommage ou interruption résultant :
- de paiements refusés ou échoués,
- de versements retardés, suspendus ou bloqués,
- de la suspension ou résiliation d’un compte Stripe,
- de rétrofacturations, fraudes ou litiges clients,
- d’actions ou décisions prises par Stripe.
Aucune clause n’exclut la responsabilité en cas de faute lourde ou intentionnelle directement imputable à la Plateforme.

9. Indemnisation
Le Boulanger s’engage à indemniser, défendre et garantir la Plateforme contre toute réclamation, perte, responsabilité, dommage ou frais (y compris frais juridiques) résultant :
- des opérations de paiement,
- des remboursements, litiges ou rétrofacturations,
- des réclamations clients liées aux transactions,
- du non-respect des obligations fiscales, réglementaires ou de protection des consommateurs.

10. Disponibilité du service
Le service est fourni « en l’état » et « selon disponibilité ». Nous ne garantissons pas un accès ininterrompu ou sans erreur.

11. Suspension & résiliation
Nous pouvons suspendre ou résilier l’accès si :
- les présentes Conditions sont violées,
- des informations requises sont manquantes ou inexactes,
- une obligation légale ou réglementaire l’exige.
Le Boulanger peut clôturer son compte à tout moment.

12. Modifications
Nous pouvons mettre à jour les présentes Conditions. Les changements importants nécessiteront une nouvelle acceptation avant de continuer à utiliser le service.

13. Droit applicable & juridiction
Les présentes Conditions sont régies par les lois de la province de l’Ontario et les lois fédérales canadiennes applicables.
Tout litige relève de la compétence exclusive des tribunaux de l’Ontario (Canada), sauf dispositions impératives contraires.

14. Contact
Digi-bakery.com
Courriel : support@digi-bakery.com
    `.trim(),
  },
};